import React, { memo, useCallback, useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { DatePicker } from '@Sage/hoi-poi-ui';

import { getLiteral } from 'utils/getLiteral';
import { formatDate, getDateFormatPlaceholder } from 'utils/dates';

const propTypes = {
    autoFocus: PropTypes.bool,
    label: PropTypes.any,
    onChange: PropTypes.func,
    value: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string]),
    className: PropTypes.string,
    error: PropTypes.any,
    mandatory: PropTypes.bool,
    readOnly: PropTypes.bool,
    hidden: PropTypes.bool,
    description: PropTypes.string,
    labelMode: PropTypes.string,
    minDate: PropTypes.instanceOf(Date),
    maxDate: PropTypes.instanceOf(Date),
    isClearable: PropTypes.bool,
    isFullWidth: PropTypes.bool,
    placeholderReadOnly: PropTypes.string,
};

const DateField = ({
    className,
    fieldId,
    hidden,
    mandatory,
    readOnly,
    label,
    hint,
    description,
    value,
    shouldRenderField,
    onChange,
    error,
    labelMode,
    minDate,
    maxDate,
    isClearable = true,
    isFullWidth = true,
    placeholderReadOnly,
    autoFocus,
    forceFocus,
    isEnriched = false,
}) => {
    const lang = useSelector((state) => state.config?.userData?.langISOInterface);
    const [date, setDate] = useState(value ? new Date(value) : null);
    const prevValue = useRef(value);
    const userDateFormat = hint || getDateFormatPlaceholder();

    useEffect(() => {
        if (value !== date && prevValue.current !== value) {
            setDate(value ? new Date(value) : null);
        }
        prevValue.current = value;
    }, [date, value]);

    const handleChange = useCallback(
        (newDate) => {
            setDate(newDate);
            onChange && onChange(newDate);
        },
        [onChange],
    );

    if (hidden) return null;
    if (shouldRenderField && !shouldRenderField(fieldId)) return null;

    return (
        <DatePicker
            className={className || 'fm-field-date'}
            label={label}
            labelMode={labelMode}
            hint={description}
            placeholder={userDateFormat}
            placeholderReadOnly={placeholderReadOnly || userDateFormat}
            inputMaskFormat={userDateFormat}
            isRequired={mandatory}
            isReadOnly={readOnly}
            onChange={handleChange}
            value={date}
            error={error}
            lang={lang}
            minDate={minDate}
            maxDate={maxDate}
            isClearable={isClearable}
            calendarButtonLabel={getLiteral('label_today')}
            formatDate={(date) => formatDate(date, 'L')}
            isFullWidth={isFullWidth}
            autoFocus={autoFocus || forceFocus}
            isEnriched={isEnriched}
        />
    );
};

DateField.displayName = 'DateField';
DateField.propTypes = propTypes;

export default memo(DateField);
